import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import CopyButton from '@/components/buttons/CopyButton.tsx';
import useGetBookingTransferTitle from '@/features/dashboard/bookingChanges/hooks/useGetBookingTransferTitle.ts';

interface TransferTitlePillProps extends React.HTMLAttributes<HTMLDivElement> {
  clientId: string;
  tourId: string;
}

/**
 * Tytuł przelewu przy nagłówku „Płatności”. Klasy pigułki za `Chip`, ale
 * `Chip` niesie tylko jeden napis, a tu stoją etykieta, wartość i przycisk.
 *
 * Błąd i brak tytułu nie rysują niczego — to dopisek do nagłówka, nie karta,
 * więc nie ma gdzie postawić komunikatu.
 */
const TransferTitlePill = ({
  clientId,
  tourId,
  className,
  ...props
}: TransferTitlePillProps) => {
  const {
    data: title,
    isLoading,
    isError,
  } = useGetBookingTransferTitle(clientId, tourId);

  if (isLoading) {
    return (
      <Skeleton
        className={cn(
          'h-8 w-full rounded-full',
          className,
        )}
      />
    );
  }

  if (isError || !title) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2 rounded-full border border-neutral-200 bg-basic-white py-0.5 pl-3 pr-0.5 text-xs font-medium leading-none text-neutral-700',
        className,
      )}
      {...props}
    >
      <span className="shrink-0 text-neutral-500">Tytuł do przelewu</span>
      <span
        title={title}
        className="font-mono truncate text-[13px] font-semibold tabular-nums text-ink"
      >
        {title}
      </span>
      <span className="ml-auto shrink-0">
        <CopyButton value={title} />
      </span>
    </div>
  );
};

export default TransferTitlePill;
